import { isNil } from 'lodash';
import { flow, values } from 'lodash/fp';
import { useTranslation } from 'react-i18next';

import { CustomThemeToken } from '@/theme';
import { useStores } from '@/hooks/use-stores';
import { useMeasurement } from '@/hooks/useMeasurement';
import { comboCompressorAndPowerSensorDeviceTypes, DeviceType } from '@/constants/deviceTypeEnum';
import { DeviceConnectivity } from '@/constants/enums';
import { CommandsActions } from '@/constants/command-actions.enum';
import { MetricType } from '@/constants/metric-type.enum';
import { metricsDetails } from '@/constants';

import { MetricsNames } from '../../MetricsNames';
import { isValueHighlighted } from '../../utils/utils';
import { DUMMY_MODEL_IDS } from '../nodeWidget/EditAssetDrawer/EditAssetDrawer.utils';

export const getBorderInfo = (
  isSelected: boolean,
  inMaintenance: boolean,
  isStopped: boolean,
  isDummyCompressor: boolean,
  token: CustomThemeToken,
): {
  fillColor: string;
  borderColor: string;
  textColor: string;
  headerText: string;
} => {
  if (inMaintenance) {
    return {
      fillColor: isSelected ? token.Ecoplant_Compressor.colorBGSelected : token.Ecoplant_Compressor.colorBGMaintenance,
      borderColor: token.colorWarningBorder,
      textColor: token.colorWarningTextHover,
      headerText: 'maintenance',
    };
  }

  if (isStopped) {
    return {
      fillColor: isSelected ? token.Ecoplant_Compressor.colorBGSelected : token.Ecoplant_Compressor.colorBGStopped,
      borderColor: token.colorErrorBorder,
      textColor: token.colorErrorTextHover,
      headerText: 'STOPPED',
    };
  }

  if (isDummyCompressor) {
    return {
      fillColor: isSelected
        ? token.Ecoplant_Compressor.colorBGSelected
        : token.Ecoplant_Compressor['colorBGmissing spec'],
      borderColor: token.colorPrimaryBorder,
      textColor: token.colorPrimaryText,
      headerText: 'missingSpec',
    };
  }
};

export const useMapViewMetrics = entity => {
  const { hp } = entity;
  const { stationStore } = useStores();
  const { t } = useTranslation();
  const { tvalue, tunit, measurementSystem } = useMeasurement();
  const onDevices = stationStore.currentStation?.devices?.filter(device => entity.onDevices.includes(device.deviceId));
  const firstPowerSensor = getFirstPowerSensor(entity, onDevices);
  const firstFlowSensor = getFirstFlowSensor(entity, onDevices);
  const firstPowerSensorKw = getPowerSensorKw(firstPowerSensor);
  const flowValue = useFlowValue(entity, firstFlowSensor, firstPowerSensorKw);

  const ratedHp = tvalue(hp, 'hp', measurementSystem, 'IMPERIAL');

  return [
    {
      name: MetricsNames.HorsePower,
      value: ratedHp,
      unit: `${tunit('ratedHP')} (${t('Rated')})`,
      highlight: isValueHighlighted(MetricsNames.HorsePower),
    },
    {
      name: MetricsNames.Power,
      value: getPowerValue(entity, stationStore.currentStation?.id, firstPowerSensorKw, tvalue),
      unit: tunit('kW'),
    },
    {
      name: MetricsNames.Flow,
      value: flowValue,
      unit: 'm^3/min',
    },
  ];
};

export const isDummyCompressor = (deviceId: string, stationStore) => {
  return (
    stationStore.currentStation?.devices?.filter(device => device.deviceId === deviceId)[0]?.deviceModelId ===
    DUMMY_MODEL_IDS.dummyCompressor
  );
};

export const getSliderPoints = t => [
  { value: 0, label: t('CLOSE') },
  { value: 20 },
  { value: 40 },
  { value: 60 },
  { value: 80 },
  { value: 100, label: t('OPEN') },
];

export const getFirstPowerSensor = (entity, onDevices) => {
  return (
    onDevices.find(device => device.deviceType === DeviceType.PowerSensor) ||
    (comboCompressorAndPowerSensorDeviceTypes.includes(entity?.deviceType) && entity)
  );
};

export const getPowerSensorKw = (powerSensor): number => {
  if (!isNil(powerSensor) && isNil(powerSensor.powerKw)) return undefined;
  if (isNil(powerSensor)) return null;
  else return powerSensor.powerKw;
};

export const getFirstFlowSensor = (_entity, onDevices) => {
  return onDevices.find(device => device.deviceType === DeviceType.FlowSensor);
};

/**
 * Get raw flow value (in m3/min) without unit transformation.
 * This implements the core logic for flow value calculation with fallback chain.
 */
export const getRawFlowM3Min = (entity, firstFlowSensor, powerSensorKw) => {
  // Priority 1: Flow from dedicated FlowSensor device
  if (!isNil(firstFlowSensor?.flowM3Min)) return firstFlowSensor.flowM3Min;

  // Priority 2: Flow directly on entity
  if (!isNil(entity?.flowM3Min)) return entity.flowM3Min;

  // Priority 3: Estimated flow (only if power data available)
  if (!isNil(entity?.estimatedFlowM3Min)) {
    if (isNil(powerSensorKw) && isNil(entity.estimatedKw)) return null;
    else return entity.estimatedFlowM3Min;
  }

  return null;
};

export const useFlowValue = (entity, firstFlowSensor, powerSensorKw) => {
  const { tvalue } = useMeasurement();
  const rawValue = getRawFlowM3Min(entity, firstFlowSensor, powerSensorKw);

  // For display, if estimated flow has no power data, show 'N/A'
  if (!isNil(entity?.estimatedFlowM3Min) && rawValue === null) {
    return tvalue('N/A', 'm^3/min');
  }

  return rawValue !== null ? tvalue(rawValue, 'm^3/min') : null;
};

/**
 * Get raw power value (in kW) without unit transformation.
 * This implements the core logic for power value calculation.
 */
export const getRawPowerKw = (entity, currentStationId, powerKw) => {
  // BAC-6729: Saputo station wants to display 0 kW instead of N/A on disconnected compressors.
  if (
    currentStationId === '056c84e5-bd75-4d58-992c-68ddb0af61f3' &&
    entity.connectivity === DeviceConnectivity.Disconnected
  ) {
    return 0;
  } else {
    return powerKw ?? entity.estimatedKw;
  }
};

export const getPowerValue = (entity, currentStationId, powerKw, tvalue) => {
  const rawValue = getRawPowerKw(entity, currentStationId, powerKw);
  return tvalue(rawValue, 'kw');
};

export const useMaxSetpointTitle = entity => {
  const { t } = useTranslation();
  return t(isNil(entity.maximumPressureBar) ? 'VSD_TARGET' : 'Unload');
};

const getMetricTypeUnit = metricType => metricType.split(/(?=[A-Z])/)?.pop();

export const useMaxSetpointValue = entity => {
  const { tvalue } = useMeasurement();

  const value = entity?.maximumPressureBar ?? entity?.VSDPressureSetpointTargetBar;
  return tvalue(Number(value), 'bar');
};

export const useSetpointValue = (entity, metricType = MetricType.MaximumPressureBar) => {
  const { tvalue } = useMeasurement();
  return tvalue(Number(entity?.[metricType]?.toFixed(2)), getMetricTypeUnit(metricType));
};

export const useMinSetpointValue = (entity, metricType = MetricType.MinimumPressureBar) => {
  const { tvalue } = useMeasurement();
  const value = metricType === MetricType.MinimumPressureBar ? entity?.[metricType] : entity?.[metricType]?.toFixed(2);
  return tvalue(Number(value), getMetricTypeUnit(metricType));
};

export const useCentrifugalTargetSetpointValue = entity => {
  const { tvalue } = useMeasurement();
  return tvalue(Number(entity.centrifugalTargetSetpoint), 'bar');
};

export const hasOnDeviceOfType = (onDevices, deviceType) => {
  return onDevices.some(onDevice => onDevice.deviceType === deviceType);
};

export const hasOnDeviceOfTypes = (onDevices, deviceTypes) => {
  return onDevices.some(onDevice => deviceTypes.includes(onDevice.deviceType));
};

export const useWaterTemperatureValue = (onDevices, waterTemperatureType) => {
  const { tvalue } = useMeasurement();
  const waterTemperatureSensors = onDevices.filter(
    onDevice => onDevice.deviceType === DeviceType.WaterTemperatureSensor,
  );
  const rawValue = waterTemperatureSensors.map(sensor => sensor[waterTemperatureType]).reduce((a, b) => a || b, null);
  return tvalue(rawValue, metricsDetails[waterTemperatureType].unit);
};

export const getWaterFlowValue = (onDevices, tvalue) => {
  const waterFlowDevice = onDevices.find(onDevice => onDevice.deviceType === DeviceType.WaterFlowSensor);
  return tvalue(waterFlowDevice[MetricType.WaterFlowM3Min], metricsDetails[MetricType.WaterFlowM3Min].unit);
};

export const getValueForDeviceType = (
  onDevices,
  tvalue,
  deviceType: DeviceType | DeviceType[],
  metricType: MetricType,
  entity,
) => {
  const deviceTypes = Array.isArray(deviceType) ? deviceType : [deviceType];
  const device = onDevices.find(onDevice => deviceTypes.includes(onDevice.deviceType)) ?? entity;
  return tvalue(device[metricType], metricsDetails[metricType].unit);
};

export const getWaterPressureValues = (onDevices, tvalue, metricType) => {
  const waterPressureDevice = onDevices.find(onDevice => onDevice.deviceType === DeviceType.WaterPressureSensor);
  return tvalue(waterPressureDevice[metricType], metricsDetails[metricType].unit);
};

export const formatDate = dateString => {
  if (dateString) {
    const date = new Date(dateString);
    const month = new Intl.DateTimeFormat('en', { month: 'long' }).format(date);
    const day = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(date);
    const year = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(date);
    const formattedDate = `${month} ${day}, ${year}`;
    return formattedDate;
  }
  return 'Date Not Valid';
};

const deviceTypeSliderPointsLabels = {
  [DeviceType.AerationBasin]: { target: 'Target', load: 'Load', unload: 'Unload' },
  [DeviceType.Blower]: { target: 'Target', load: '', unload: '' },
  default: { target: 'Target', load: 'Load', unload: 'Unload' },
};

export const getDeviceSliderPoints = (targetValue, minValue, maxValue, t, tvalue, type: DeviceType, unit = 'bar') => {
  return {
    target: {
      value: targetValue ? tvalue(Number(targetValue), unit) : null,
      label: t(deviceTypeSliderPointsLabels[type]?.target ?? deviceTypeSliderPointsLabels.default.target),
      highlighted: true,
      isTarget: true,
    },
    load: {
      value: minValue ? tvalue(Number(minValue), unit) : null,
      label: deviceTypeSliderPointsLabels[type]?.load ?? deviceTypeSliderPointsLabels.default.load,
    },
    unload: {
      value: maxValue ? tvalue(Number(maxValue), unit) : null,
      label: deviceTypeSliderPointsLabels[type]?.unload ?? deviceTypeSliderPointsLabels.default.unload,
    },
  };
};

export const getDeviceSliderPointsArray = flow(getDeviceSliderPoints, values);

export const getCompressorSliderPointsByActions = (device, mainSliderPoints) => {
  if (
    device?.actions.includes(CommandsActions.LoadUnloadTargetBar) ||
    device?.actions.includes(CommandsActions.LoadUnloadTargetPsi)
  ) {
    return [mainSliderPoints.load, mainSliderPoints.unload, mainSliderPoints.target];
  } else if (
    device?.actions.includes(CommandsActions.UnloadTargetPsi) ||
    device?.actions.includes(CommandsActions.UnloadTargetBar)
  ) {
    return [mainSliderPoints.unload, mainSliderPoints.target];
  } else if (
    device?.actions.includes(CommandsActions.LoadTargetPsi) ||
    device?.actions.includes(CommandsActions.LoadTargetBar)
  ) {
    return [mainSliderPoints.load, mainSliderPoints.target];
  } else if (
    device?.actions.includes(CommandsActions.TargetPsi) ||
    device?.actions.includes(CommandsActions.TargetBar)
  ) {
    return [mainSliderPoints.target];
  }
  return [];
};

export const isVSDCompressor = entity =>
  [DeviceType.VariableSpeedDrive, DeviceType.VariableSpeedDriveAndPowerSensor].includes(entity.deviceType);

export const useGetCompressorSliderPoints = (entity, hasNewActionsFeature) => {
  const { devicesStore } = useStores();
  const device = devicesStore.getDeviceById(entity.deviceId);
  const { t } = useTranslation();
  const { tvalue } = useMeasurement();
  const minSetpointValue = useMinSetpointValue(entity);
  const maxSetpointValue = useMaxSetpointValue(entity);
  const maxSetpointTitle = useMaxSetpointTitle(entity);
  const centrifugalTargetSetpointValue = useCentrifugalTargetSetpointValue(entity);

  let mainSliderPoints;

  const isCentrifugal = [DeviceType.Centrifugal, DeviceType.CentrifugalAndPowerSensor].includes(entity.deviceType);
  const isVSD = isVSDCompressor(entity);

  if (isVSD) {
    mainSliderPoints = getDeviceSliderPoints(
      entity.VSDPressureSetpointTargetBar,
      entity.minimumPressureBar,
      entity.maximumPressureBar,
      t,
      tvalue,
      entity.deviceType,
    );
  }

  if (isCentrifugal) {
    mainSliderPoints = getDeviceSliderPoints(
      entity.centrifugalTargetSetpoint,
      entity.minimumPressureBar,
      entity.maximumPressureBar,
      t,
      tvalue,
      entity.deviceType,
    );
  }

  if (isCentrifugal || isVSD) {
    if (hasNewActionsFeature) {
      mainSliderPoints = getCompressorSliderPointsByActions(device, mainSliderPoints);
    } else {
      mainSliderPoints = [mainSliderPoints?.load, mainSliderPoints?.unload, mainSliderPoints?.target];
    }
  } else mainSliderPoints = [];

  // Fallback if no specific points for VSD or Centrifugal
  if (mainSliderPoints.length === 0) {
    mainSliderPoints = [
      ...(!isNil(entity.minimumPressureBar) ? [{ value: minSetpointValue, label: t('Load') }] : []),
      ...(!isNil(entity.maximumPressureBar)
        ? [{ value: maxSetpointValue, label: maxSetpointTitle, isTarget: maxSetpointTitle === t('VSD_TARGET') }]
        : []),
    ];
    if (isCentrifugal && !isNil(entity.centrifugalTargetSetpoint)) {
      mainSliderPoints.push({
        value: centrifugalTargetSetpointValue,
        label: t('Target Setpoint'),
        highlighted: true,
        isTarget: true,
      });
    }
  }

  return mainSliderPoints;
};

export const getFlowTooltip = (entity, t) => {
  return isNil(entity.estimatedFlowM3Min) ? t('Sensor based') : t('kW based calculation');
};

export const isValidValue = value => !isNil(value) && !isNaN(value) && value !== 'N/A';

export const getIsStopped = (poweredOnMetricPresent, powerChangeSupportedAction, poweredOnState) =>
  poweredOnMetricPresent && powerChangeSupportedAction && !poweredOnState;
